import { config } from 'config/config';
import moment from 'moment';
import { IResourceList } from 'src/components/challenge/ChallengeInterfaces';
import {
  ResourceTypeDetailEnum,
  ResourceTypeEnum,
  UserResourceEnum,
} from 'src/shared/enums';
import {
  IChallenge,
  IChallengeEvent,
  IEventStatus,
  IEventWatchAgain,
  IUserStatus,
} from 'src/shared/models';
import {
  isAttendAvailable,
  isEventExpired,
  isEventInProgress,
  isInscriptionAvailable,
} from 'utils';

const { DEFAULT_TIMEZONE } = config.APP;

const {
  CANCELED,
  CAPACITY,
  CITY,
  CODE,
  ENABLED,
  EVENT_CONTENT,
  EVENT_END,
  EVENT_START,
  FULL,
  IN_PROGRESS,
  LOCATION,
  MAPS_URL,
  ONLINE,
  PRE_INSCRIPTION_TEXT,
  URL,
  WATCH_AGAIN_YT_ID,
  WATCH_AGAIN_ONLY_ATTENDED_USERS,
} = ResourceTypeDetailEnum;

const { ATTEND, INSCRIPTION } = ResourceTypeEnum;
const { DONE, SIGNED, WAITING_LIST, CONFIRM_ATTEND } = UserResourceEnum;

export const buildChallengeEvents = (
  challenge: IChallenge,
  events: IResourceList[]
): IChallengeEvent[] => {
  const performedStatus = [SIGNED, WAITING_LIST, DONE, CONFIRM_ATTEND];
  const challengeEvents: IChallengeEvent[] = [];
  let eventType: undefined | 'online' | 'onSite';
  let startEventDate: moment.Moment;
  let endEventDate: moment.Moment;
  let status: IEventStatus;
  let attend: IUserStatus;
  let inscription: IUserStatus;
  let watchAgain: IEventWatchAgain;
  let endDate: moment.Moment;
  let startDate: moment.Moment;

  const { startDate: _startDate, endDate: _endDate, startVisDate } = challenge;

  endDate = moment.utc(_endDate).utcOffset(DEFAULT_TIMEZONE, false);
  startDate = moment.utc(_startDate).utcOffset(DEFAULT_TIMEZONE, false);

  const _events = events.filter((event) => event.status);

  _events.forEach((event) => {
    const {
      resourceDetailList,
      resourceList,
      i18nList,
      name,
      survey,
      idResource,
    } = event;

    let challengeEvent: IChallengeEvent;
    let isMain = false || _events.length == 1;
    let points = survey?.score.points || 0;

    resourceList
      .filter((resource) => resource.status)
      .forEach((_detail) => {
        const {
          idResourceType: { idResourceType },
          score,
          idResource,
          usersM2MList: [usersM2MList],
        } = _detail;

        const statusResource = usersM2MList?.statusResource as UserResourceEnum;
        const isPerformed = performedStatus.includes(statusResource);

        points += Boolean(score?.points) ? score.points : 0;
        isMain = isMain || isPerformed;

        const data = {
          points: score?.points,
          idResource,
          usersM2MList,
          isPerformed,
          statusResource,
          inWaitingList: statusResource === UserResourceEnum.WAITING_LIST,
        };

        switch (idResourceType) {
          case ATTEND:
            attend = {
              ...attend,
              ...data,
            };
            break;
          case INSCRIPTION:
            inscription = {
              ...inscription,
              ...data,
            };
            break;
        }
      });

    resourceDetailList
      .filter((resource) => resource.status)
      .forEach((_detail) => {
        const { idResourceTypeD } = _detail.idResourceTypeD;

        switch (idResourceTypeD) {
          case ENABLED:
            status = {
              ...status,
              isEnabled: _detail.value === 'true',
            };
            break;
          case IN_PROGRESS:
            status = {
              ...status,
              isInProgress: _detail.value === 'true',
            };
            break;
          case FULL:
            status = {
              ...status,
              isFull: _detail.value === 'true',
            };
            break;
          case CANCELED:
            status = {
              ...status,
              isCanceled: _detail.value === 'true',
            };
            break;
          case ONLINE:
            eventType = _detail.value === 'true' ? 'online' : 'onSite';
            break;
          case LOCATION:
            challengeEvent = {
              ...challengeEvent,
              location: _detail.value,
            };
            break;
          case EVENT_START:
            startEventDate = moment
              .utc(_detail.value)
              .utcOffset(DEFAULT_TIMEZONE, false);

            break;
          case EVENT_END:
            endEventDate = moment
              .utc(_detail.value)
              .utcOffset(DEFAULT_TIMEZONE, false);
            break;
          case CITY:
            challengeEvent = {
              ...challengeEvent,
              city: _detail.value,
            };
            break;
          case CAPACITY:
            challengeEvent = {
              ...challengeEvent,
              capacity: _detail.value,
            };
            break;
          case EVENT_CONTENT:
            challengeEvent = {
              ...challengeEvent,
              content: _detail.value,
            };
            break;
          case URL:
            challengeEvent = {
              ...challengeEvent,
              url: _detail.value,
            };
            break;
          case CODE:
            challengeEvent = {
              ...challengeEvent,
              code: _detail.value,
            };
            break;
          case PRE_INSCRIPTION_TEXT:
            challengeEvent = {
              ...challengeEvent,
              preInscriptionText: _detail.value,
            };
            break;
          case MAPS_URL:
            challengeEvent = {
              ...challengeEvent,
              urlMap: _detail.value,
            };
            break;
          case WATCH_AGAIN_YT_ID:
            watchAgain = {
              ...watchAgain,
              videoID: _detail.value?.trim(),
              isInformed: Boolean(_detail.value?.trim()),
            };
            break;
          case WATCH_AGAIN_ONLY_ATTENDED_USERS:
            watchAgain = {
              ...watchAgain,
              isPublic: _detail.value !== 'true',
            };
            break;
        }
      });

    watchAgain = {
      ...watchAgain,
      id: `challenge-watch-again-${idResource}`,
      shouldRender:
        isEventExpired(endEventDate) &&
        watchAgain?.isInformed &&
        (attend.isPerformed || watchAgain?.isPublic),
    };

    if (Boolean(startEventDate)) {
      attend = {
        ...attend,
        isAvailable: isAttendAvailable(startEventDate, endEventDate),
      };

      inscription = {
        ...inscription,
        isAvailable: isInscriptionAvailable(startVisDate, startEventDate),
      };
    }

    status = {
      ...status,
      isExpired: isEventExpired(endEventDate),
      isInProgress: isEventInProgress(startEventDate, endEventDate),
      isOnline: eventType == 'online',
      isOnsite: eventType == 'onSite',
    };

    challengeEvent = {
      ...challengeEvent,
      watchAgain,
      idResource,
      status,
      eventType,
      dates: {
        startDate,
        endDate,
        attendDate: startEventDate,
        endEventDate,
        startEventDate,
      },
      attend,
      inscription,
      isMain,
      i18nList,
      name,
      survey,
      points,
    };

    challengeEvents.push(challengeEvent);
  });

  return challengeEvents;
};
